import { useState, useEffect, useRef } from "react";
import { createModel } from "hox";
import { enquiryService } from "../../enquiryService";
import { IEnquiryTabEnum, ISupplierPriceDialog, IEnquiryMaterialDetail, IEnquiryDetail, ISupplierOfferDetails, ISupplierMaterialDetails } from "../../enquiryTypes";
import { IMaterialDetail } from "@/views/base/material/materialService";
import { message } from "antd";
import useGlobalModel from "@/model/globalModel";
import { useLockFn } from "ahooks";
import { convertRes2Blob } from "@/utils/exportFile";
import moment from "moment";


export const useEnquiryDetailModel = createModel(function () {
    const { user: { shopCode, shopName }, setLoadingInfo } = useGlobalModel(({ user }) => [user])
    const initFormVal = {
        id: "",
        code: "",
        shopName
    };
    const [tabActive, setTabActive] = useState<IEnquiryTabEnum>(IEnquiryTabEnum.BASE_DETAIL) //tabs 选中

    //编辑缓存
    const [editFormVal, setEditFormVal] = useState<IEnquiryDetail>(initFormVal);
    const [editList, setEditList] = useState<IEnquiryMaterialDetail[]>([]);

    //新增缓存
    const [addFormVal, setAddFormVal] = useState<IEnquiryDetail>(initFormVal);
    const [addList, setAddList] = useState<IEnquiryMaterialDetail[]>([]);

    //添加商品弹框
    const [materialDialog, setMaterialDialog] = useState<boolean>(false);

    //重置页面缓存
    const resetPageCache = (mode: string) => {
        if (mode === "edit") {
            setEditFormVal(initFormVal);
            setEditList([]);
        }
        else if (mode === "add") {
            setAddFormVal(initFormVal);
            setAddList([]);
        }
        setTabActive(IEnquiryTabEnum.BASE_DETAIL);
    }

    //主单修改
    const updateFormVal = (mode: string, val?: object | null) => {
        const setFormVal = mode === "edit" ? setEditFormVal : setAddFormVal;
        setFormVal((origin: any) => val ? { ...origin, ...val } : initFormVal);
    }

    //商品子表修改
    const updateList = (mode: string, val: IEnquiryMaterialDetail[]) => {
        const setList = mode === "edit" ? setEditList : setAddList;
        setList([...val]);
    }

    //商品子表添加
    const insertMaterial = (mode: string, arr: IMaterialDetail[]) => {
        const setList = mode === "edit" ? setEditList : setAddList;
        const tempArr: string[] = [];  //记录重复配件
        setList(origin => {
            let newArr: IEnquiryMaterialDetail[] = [...origin];
            arr.forEach(material => {
                //选中的是否存在列表
                let isExist = origin.find(item => item.materialCode == material.code);
                if (!isExist) {
                    newArr.push({
                        "key": material.id,  //自己用
                        "materialId": material.id, // "物料id",
                        "materialCode": material.code, // "物料编号",
                        "replaceCode": material.replaceCode, // "替换号",
                        "materialName": material.name, // "物料名称",
                        "materialEname": material.nameEn, // "物料名称",
                        "typeCode": material.typeCode, // "物料类型",
                        "typeName": material.typeName, // "物料类型名称",
                        "unitCode": material.unitCode, // 
                        "unitName": material.unitName, // "单位",
                        "customsCode": material.customsCode, // "海关编码",
                        "num": material.num, // "询价数量",
                        "model": material.model, // "规格",
                        "note": "", // "备注"
                    })
                } else {
                    tempArr.push(material.name ?? "")
                }
            });
            return newArr;
        });
        tempArr.length && message.warning(`已过滤重复配件【${tempArr.join()}】`)
        setMaterialDialog(false);
    }

    //商品子表删除
    const removeMaterial = (mode: string, selectedRowKeys: string[]) => {
        const setList = mode === "edit" ? setEditList : setAddList;
        setList(origin => origin.filter(item => selectedRowKeys.indexOf(item.key) < 0));
    }

    //获取一条数据
    const getEnquiryDetailOne = async (id: string) => {
        let { retData } = await enquiryService.one(id);
        let { details, ...formVal } = retData;
        if (retData) {
            setEditFormVal({
                ...formVal,
                createTime:formVal.createTime ?moment(formVal.createTime):null,
                updateTime:formVal.updateTime ?moment(formVal.updateTime):null
            });
            setEditList(details?.map(item => ({ ...item, key: item.id ?? "" })) ?? []);
        }
        return retData;
    };

    //报价单新增 | 编辑
    const insertOrUpdateEnquiry = useLockFn(async (mode: string, formVal: IEnquiryDetail) => {
        const materialList = mode === "edit" ? editList : addList;
        let data = {
            ...formVal,
            shopCode,
            details: materialList,
           //createTime:formVal.createTime instanceof Object ? formVal.createTime.format('YYYY-MM-DDTHH:mm:SS') :null,
           // updateTime:formVal.updateTime instanceof Object ? formVal.updateTime.format('YYYY-MM-DDTHH:mm:SS') :null
           createTime:formVal.createTime ?moment(formVal.createTime):null,
           updateTime:formVal.updateTime ?moment(formVal.updateTime):null
        };
        //新增
        if (mode === "edit") {
            let { retData } = await enquiryService.update({ ...data, id: editFormVal.id, code: editFormVal.code ?? "" });
            getEnquiryDetailOne(editFormVal.code)
            message.success("保存成功")
            return retData;
        } else {
            let { retData } = await enquiryService.insert(data);
            message.success("新增成功,自动转换至编辑详情页")
            return retData;
        }
    })  

    //导出
    const exportEnquiry = async () => {
        setLoadingInfo({ loadingVisible: true, txt: "快速导出中" });
        try {
            let result = await enquiryService.detailExport(editFormVal.code, shopCode);
            result && setLoadingInfo({ loadingVisible: false, txt: "" });
            convertRes2Blob(result);
        } catch { setLoadingInfo({ loadingVisible: false, txt: "" }) }
    }

    //报价单明细下载模板
    const downloadTpl = async () => {
        let result = await enquiryService.downloadTpl();
        convertRes2Blob(result);
    }

    return {
        /***field***/
        editFormVal,
        editList,
        addFormVal,
        addList,
        tabActive, setTabActive,
        materialDialog, setMaterialDialog,
        /***method***/
        getEnquiryDetailOne,
        updateFormVal,
        updateList,
        removeMaterial,
        insertMaterial,
        insertOrUpdateEnquiry,
        resetPageCache,
        exportEnquiry,
        downloadTpl
    }
});